import type { ImagePickerAsset } from 'expo-image-picker';
import type { Listing, NeedMatch } from './listing';
import type { Loan } from './loan';
import type { Bonification } from './bonification';
import type { User } from './user';
import type { Profile } from './profile';

export enum CustomChannelType {
  Listing = 'listing',
  Member = 'member',
  Support = 'support',
  Ambassador = 'ambassador',
  Group = 'group',
}

export enum ChatConversationMemberRole {
  Admin = 'admin',
  Regular = 'regular',
}

export enum MessageKind {
  File = 'FILE',
  Message = 'MESG',
}

export enum CustomMessageType {
  Loan = 'loan',
  System = 'system',
  RecomPrompt = 'recom_prompt',
  LatePrompt = 'late_prompt',
  ReturnedPrompt = 'returned_prompt',
  BonifyPrompt = 'bonify_prompt',
  ConciergePrompt = 'concierge_prompt',
  Unavailability = 'unavailability',
  TransportPrompt = 'transport_prompt',
  SalePrompt = 'sale_prompt',
  PostSaleListingPrompt = 'post_sale_listing_prompt',
  FirstLoanPrompt = 'first_loan_prompt',
  LoanReciprocityPrompt = 'loan_reciprocity_prompt',
}

interface MessageBase {
  channel_url: string;
  created_at: number;
  message_id: string;
  type: MessageKind;
  user?: ChatUser;
  viewed: boolean;
  is_invisible?: boolean; // Messages we use to show a badge + send a push notif, but that we don't want to display
  is_automatic?: boolean;
  reactions?: { count: number; emoji: string; userIds: string[] }[];
  reply_to?: ReplyTo;
  is_deleted?: boolean;
  is_edited?: boolean;
}

export interface ReplyTo {
  message_id: string;
  type: MessageKind;
  user?: ChatUser;
  message?: string;
  file?: { url: string };
  is_deleted?: boolean;
}

export type TextMessageMessage = string | { i18nKey: string; ns: string; values?: Record<string, string> };

export interface TextMessage extends MessageBase {
  custom_type?: never;
  message: TextMessageMessage;
  og_tag?: {
    'og:description': string;
    'og:image': {
      url: string;
      secure_url: string;
    } | null;
    'og:title': string;
    'og:url': string;
  };
  type: MessageKind.Message;
}

export interface FileMessage extends MessageBase {
  file: {
    url: string;
  };
  type: MessageKind.File;
}

export interface LoanMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.Loan;
  data: { loanId: Loan['id']; loanStatus: Loan['status']; listingSubtype?: Listing['subtype'] };
  loan: Loan;
}

export interface SystemMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.System;
}

export interface RecomMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.RecomPrompt;
  data: { loanId: Loan['id']; loanStatus?: Loan['status'] };
  loan: Loan;
}

export interface LateMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.LatePrompt;
  data: { loanId: Loan['id']; loanStatus: Loan['status'] };
  loan: Loan;
}

export interface ReturnedMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.ReturnedPrompt;
  data: { loanId: Loan['id']; loanStatus: Loan['status'] };
  loan: Loan;
}

export interface BonifyMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.BonifyPrompt;
  data: { loanId: Loan['id']; listingId: Listing['id']; bonificationId?: Bonification['id'] };
  loan?: Loan;
}

export interface ConciergeMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.ConciergePrompt;
  data: { listingId: Listing['id'] };
}

export interface UnavailabilityMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.Unavailability;
}

export interface TransportMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.TransportPrompt;
  data: { loanId: Loan['id'] };
  loan: Loan;
}

export interface SaleMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.SalePrompt;
  data: { loanId: Loan['id'] };
  loan: Loan;
}

export interface PostSaleListingMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.PostSaleListingPrompt;
  data: { loanId: Loan['id']; listingId: Listing['id'] };
  loan: Loan;
}

export interface FirstLoanMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.FirstLoanPrompt;
  data: { loanId: Loan['id']; loanStatus: Loan['status'] };
  loan: Loan | null;
}

export interface LoanReciprocityMessage extends Omit<TextMessage, 'custom_type'> {
  custom_type: CustomMessageType.LoanReciprocityPrompt;
  data: { loanId: Loan['id'] };
  loan: Loan;
}

export type Message =
  | FileMessage
  | TextMessage
  | LoanMessage
  | SystemMessage
  | RecomMessage
  | LateMessage
  | ReturnedMessage
  | BonifyMessage
  | ConciergeMessage
  | UnavailabilityMessage
  | TransportMessage
  | SaleMessage
  | PostSaleListingMessage
  | FirstLoanMessage
  | LoanReciprocityMessage;

export const isSystemMessage = (message: Message) =>
  message.type === MessageKind.Message && message.custom_type === CustomMessageType.System;

export const isUserMessage = (message: Message) =>
  message.type === MessageKind.Message && message.custom_type === undefined;

export const isConciergeMessage = (message: Message): message is ConciergeMessage =>
  message.type === MessageKind.Message && message.custom_type === CustomMessageType.ConciergePrompt;

export const isFileMessage = (message: Message): message is FileMessage => message.type === MessageKind.File;

interface MessageCreateInputInterface {
  channel_url: Channel['channel_url'];
  isFirstMessage: boolean;
  listingUserId?: string;
  reply_to_id?: string;
}

export interface MessageCreateResponse {
  id: string;
  channel: {
    id: string;
    type: CustomChannelType;
  };
  supportReply?: {
    status: 'queued' | 'human';
    requestMessageId: string;
  };
}

interface TextMessageCreateInput extends MessageCreateInputInterface {
  message: string;
  type: MessageKind.Message;
}

interface FileMessageCreateInput extends MessageCreateInputInterface {
  file: ImagePickerAsset;
  type: MessageKind.File;
}

export type MessageCreateInput = TextMessageCreateInput | FileMessageCreateInput;
export interface ChatUser {
  firstname: string;
  nickname: string;
  profile_url: string;
  user_id: User['id'];
  trust_points?: number;
  last_seen?: Date;
  insured?: boolean;
  isSupport: boolean;
  unavailability?: { start: string; end: string }[] | null;
  role?: ChatConversationMemberRole;
}

interface DistanceResult {
  id: string;
  distance: number;
  distanceFrom?: { name?: string; icon?: number };
}

interface ChannelBase {
  channel_url: string;
  custom_type: CustomChannelType;
  last_message?: Message;
  member_count: number;
  members: ChatUser[];
  name: string;
  picture: string;
  unread_message_count: number;
  silenced?: boolean;
  silenced_until?: number;
  created_at?: number;
  warn_no_response?: boolean;
  messages?: Message[];
  listing?: Listing & { hasLender: boolean };
  linkedOffer?: Listing | null;
  borrowerCreditExhausted?: boolean;
  distance?: DistanceResult;
  loans?: Loan[];
}

export const isChannelSilenced = (channel: Pick<ChannelBase, 'silenced' | 'silenced_until'>) =>
  Boolean(channel.silenced_until ? channel.silenced_until * 1000 > Date.now() : channel.silenced);

export interface ListingChannel extends ChannelBase {
  custom_type: CustomChannelType.Listing;
  category_color: string;
  needMatch?: NeedMatch;
  listingUserId: Listing['userId'];
  listing_name: string;
  data: { listingId: Listing['id'] };
}

export interface MemberChannel extends ChannelBase {
  custom_type: CustomChannelType.Member;
  data: { userId: Profile['userId'] };
}

export interface SupportChannel extends ChannelBase {
  custom_type: CustomChannelType.Support;
  data: never;
}

export interface GroupChannel extends ChannelBase {
  custom_type: CustomChannelType.Group;
  current_user_role?: ChatConversationMemberRole;
  description?: string | null;
  data?: never;
}

export type Channel = ListingChannel | MemberChannel | SupportChannel | GroupChannel;

export interface ListingGroupHeader {
  type: 'listing_group';
  listingId: Listing['id'];
  listingName: string;
  count: number;
  unread_message_count: number;
  picture: string;
  channel_url: string;
  needMatch?: NeedMatch;
}

export interface UserGroupHeader {
  type: 'user_group';
  userId: string;
  userName: string;
  count: number;
  unread_message_count: number;
  picture: string;
  channel_url: string;
}

export type ChannelGroupHeader = ListingGroupHeader | UserGroupHeader;

export type ChannelListItem = Channel | ChannelGroupHeader;

export function isGroupHeader(item: ChannelListItem): item is ChannelGroupHeader {
  return 'type' in item;
}

export const getOneChannelDefaultInclude = [
  'messages',
  'listing',
  'distance',
  'loans',
  'recom_prompt',
  'late_prompt',
  'bonify_prompt',
  'concierge_prompt',
  'transport_prompt',
  'sale_prompt',
  'post_sale_listing_prompt',
  'first_loan_prompt',
  'loan_reciprocity_prompt',
]; // Using this as the channel URL, when opening the one-channel screen, will tell the server we want to open the chat with Partage Club support
